/**
 * Cart Store (Zustand) with ERPNext Backend
 *
 * Manages shopping cart state synced with ERPNext backend
 * Each cart item is a separate line item (base product + add-ons)
 */

import { create } from 'zustand';
import * as cartApi from '@/services/erpnextCart';
import type { Currency } from '@/types';

// Simplified cart item for frontend display
export interface CartItem {
  item_code: string;      // ERPNext item code
  item_name: string;
  qty: number;
  rate: number;           // Price per unit
  amount: number;         // Total (qty × rate)
  image?: string;
  currency: Currency;
}

interface CartState {
  // State
  items: CartItem[];
  total: number;
  totalQty: number;
  currency: Currency;
  isLoading: boolean;
  error: string | null;
  quotationId: string | null;

  // Actions
  fetchCart: () => Promise<void>;
  addItem: (itemCode: string, qty: number, notes?: string) => Promise<void>;
  updateQuantity: (itemCode: string, qty: number) => Promise<void>;
  removeItem: (itemCode: string) => Promise<void>;
  clearCart: () => Promise<void>;
  checkout: () => Promise<string>; // Returns Sales Order ID

  // Computed
  getItemCount: () => number;
  getTotal: () => number;
}

export const useCartStore = create<CartState>((set, get) => ({
  // Initial state
  items: [],
  total: 0,
  totalQty: 0,
  currency: 'CAD',
  isLoading: false,
  error: null,
  quotationId: null,

  /**
   * Fetch cart from backend
   */
  fetchCart: async () => {
    set({ isLoading: true, error: null });

    try {
      const response = await cartApi.getCart();
      const cart = response.doc;

      // Transform ERPNext cart items to frontend format
      const items: CartItem[] = cart.items.map((item) => ({
        item_code: item.item_code,
        item_name: item.item_name,
        qty: item.qty,
        rate: item.rate,
        amount: item.amount,
        image: item.image,
        currency: cart.currency as Currency,
      }));

      set({
        items,
        total: cart.grand_total,
        totalQty: cart.total_qty,
        currency: cart.currency as Currency,
        quotationId: cart.name,
        isLoading: false,
      });
    } catch (error) {
      console.error('Fetch cart error:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to load cart',
        isLoading: false,
        // Set empty cart on error
        items: [],
        total: 0,
        totalQty: 0,
      });
    }
  },

  /**
   * Add item to cart
   */
  addItem: async (itemCode: string, qty: number, notes?: string) => {
    set({ isLoading: true, error: null });

    try {
      await cartApi.updateCartItem(itemCode, qty, notes);
      // Refresh cart from backend
      await get().fetchCart();
    } catch (error) {
      console.error('Add item error:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to add item',
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Update item quantity
   */
  updateQuantity: async (itemCode: string, qty: number) => {
    set({ isLoading: true, error: null });

    try {
      await cartApi.updateCartItem(itemCode, qty);
      // Refresh cart from backend
      await get().fetchCart();
    } catch (error) {
      console.error('Update quantity error:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to update quantity',
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Remove item from cart
   */
  removeItem: async (itemCode: string) => {
    set({ isLoading: true, error: null });

    try {
      await cartApi.removeCartItem(itemCode);
      // Refresh cart from backend
      await get().fetchCart();
    } catch (error) {
      console.error('Remove item error:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to remove item',
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Clear entire cart
   */
  clearCart: async () => {
    set({ isLoading: true, error: null });

    try {
      await cartApi.clearCart();
      set({
        items: [],
        total: 0,
        totalQty: 0,
        quotationId: null,
        isLoading: false,
      });
    } catch (error) {
      console.error('Clear cart error:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to clear cart',
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Checkout - converts cart to Sales Order
   */
  checkout: async () => {
    set({ isLoading: true, error: null });

    try {
      const salesOrderId = await cartApi.placeOrder();

      // Clear local cart state after successful checkout
      set({
        items: [],
        total: 0,
        totalQty: 0,
        quotationId: null,
        isLoading: false,
      });

      return salesOrderId;
    } catch (error) {
      console.error('Checkout error:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to place order',
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Get total item count
   */
  getItemCount: () => {
    return get().totalQty;
  },

  /**
   * Get cart total
   */
  getTotal: () => {
    return get().total;
  },
}));
